import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

// ============================================
// GRAPH PERMISSIONS CONTEXT
// ============================================
//
// This context provides frontend-level permission checks for graph operations.
// It enables instant UX feedback by caching module permissions in React state.
//
// ARCHITECTURE:
// - Frontend Layer (this file): Instant permission checks using cached module config
// - Backend Layer (supabase-client): Optional server-side validation with skipModuleCheck
// - Database Layer: Check constraints enforce permissions (final security guarantee)
//
// USAGE:
// 1. Wrap your app with GraphPermissionsProvider and pass permission check functions
// 2. useCreateNode and useCreateEdge will automatically use these permissions
// 3. If provider is not available, gracefully degrades to backend checks
//
// BENEFITS:
// - 0ms permission checks (no network)
// - Instant error feedback before API call
// - Skips redundant backend permission queries (50% faster)
// - Optional - works without provider (backend fallback)
//
// SEE: MODULE_PERMISSIONS_OPTIMIZATION.md for full implementation details
//
// ============================================

export interface GraphPermissions {
  /**
   * Check if a node type is allowed for the current tenant
   * Returns false if module config not loaded or node type not enabled
   */
  isNodeTypeAllowed: (nodeType: string) => boolean;

  /**
   * Check if an edge type is allowed for the current tenant
   * Returns false if module config not loaded or edge type not enabled
   */
  isEdgeTypeAllowed: (edgeType: string) => boolean;
}

/**
 * Context for graph permissions
 * Undefined by default - allows optional usage
 */
const GraphPermissionsContext = createContext<GraphPermissions | undefined>(undefined);

export interface GraphPermissionsProviderProps {
  children: ReactNode;
  isNodeTypeAllowed: (nodeType: string) => boolean;
  isEdgeTypeAllowed: (edgeType: string) => boolean;
}

/**
 * Provider for graph permissions context
 *
 * Typically wraps the entire app and receives permission check functions
 * from a ModuleContext that caches the tenant's module configuration.
 *
 * @example
 * ```typescript
 * function App() {
 *   const { isNodeTypeAllowed, isEdgeTypeAllowed } = useModules();
 *
 *   return (
 *     <GraphPermissionsProvider
 *       isNodeTypeAllowed={isNodeTypeAllowed}
 *       isEdgeTypeAllowed={isEdgeTypeAllowed}
 *     >
 *       <YourApp />
 *     </GraphPermissionsProvider>
 *   );
 * }
 * ```
 */
export function GraphPermissionsProvider({ children, isNodeTypeAllowed, isEdgeTypeAllowed }: GraphPermissionsProviderProps) {
  return (
    <GraphPermissionsContext.Provider
      value={{
        isNodeTypeAllowed,
        isEdgeTypeAllowed,
      }}
    >
      {children}
    </GraphPermissionsContext.Provider>
  );
}

/**
 * Hook to access graph permissions
 *
 * Returns undefined if GraphPermissionsProvider is not available.
 * This allows graceful degradation - code can check for permissions
 * and fall back to backend validation if not available.
 *
 * @example
 * ```typescript
 * function useCreateNode() {
 *   const permissions = useGraphPermissions();
 *
 *   return useMutation({
 *     mutationFn: (input: CreateNodeInput) => {
 *       // Frontend check (if provider available)
 *       if (permissions && !permissions.isNodeTypeAllowed(input.type)) {
 *         throw new Error('Node type not enabled');
 *       }
 *
 *       // Skip backend check if frontend validated
 *       return createNode(input, { skipModuleCheck: !!permissions });
 *     },
 *   });
 * }
 * ```
 */
export function useGraphPermissions(): GraphPermissions | undefined {
  return useContext(GraphPermissionsContext);
}
